import { Component, Inject, Input, OnChanges, OnInit } from 'angular-ts-decorators';

import { fetchGroupPrice } from '@gn-audio/core/api/pricing/fetch-group-price';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { IProductWithAttributes } from '@gn-audio/core/api/product-catalogue';
import { fetchSiteFeatures, ISiteFeature, SiteFeature } from '@gn-audio/core/api/site-settings';
import { AvailabilityState, ProductAttributeKey } from '@gn-audio/core/common';
import { BasketEvent } from '@gn-audio/sfcc-basket/enums';
import { IBasketEventBus, ISFCCProductItem } from '@gn-audio/sfcc-basket/models';
import { isProductAllowedForEngraving } from '@gn-audio/sfcc-basket/plugins/basket-engraving/utilities';

import template from './hero-customizer-v3-engraving.component.html';

declare var BasketEventBus: IBasketEventBus;

@Component({
  selector: 'heroCustomizerV3Engraving',
  template,
})
export class HeroCustomizerV3EngravingComponent implements OnInit, OnChanges {
  public static $inject: ReadonlyArray<string> = [];

  public isEnabled = false;
  public isEngravingAllowed = false;
  public engravingMessage = '';

  @Input() public readonly group?: IProductWithAttributes;
  @Input() public readonly is3dRenderingEnabled?: boolean;
  @Input('&') public readonly onEngravingMessageChange?: (args: { message: string; }) => void;

  // Have to use $scope in component to run $digests manually
  // because we integrate with application that is outside AngularJS runtime.
  constructor(@Inject('$scope') private readonly $scope: ng.IScope) { }

  public get hasEngravingMessage() {
    return !!this.engravingMessage;
  }

  public async ngOnInit() {
    this.isEnabled = await this.isEngravingFeatureOn();
    if (!this.isEnabled) {
      return;
    }

    // At this point Basket event bus might not be available,
    // so we will subscribe to correspondent event and then subscribe.
    if (typeof BasketEventBus === 'undefined') {
      window.addEventListener(BasketEvent.EventBusReady, this.subscribe);
    } else {
      this.subscribe();
    }
  }

  public async ngOnChanges() {
    this.isEnabled = await this.isEngravingFeatureOn();
    await this.handleChanges();
    this.$scope.$applyAsync();
  }

  public get engravingRenderingUuid(): string {
    return this.group?.attributes?.find(
      (attribute) => attribute.key === ProductAttributeKey.Meta3dEngravingRenderingUuid
    )?.value;
  }

  private subscribe = () => {
    BasketEventBus.subscribe(BasketEvent.AddToBasket, this.clearEngraving);
  };

  private clearEngraving = () => {
    // Empty engraving message when product is added to the basket.
    this.applyEngraving('');
  };

  public async showEngravingPopup() {
    if (typeof BasketEventBus === 'undefined') {
      return;
    }

    const engraving3dRenderingUuid = this.is3dRenderingEnabled ? this.engravingRenderingUuid : null;

    const engravingPopupData = { c_engravingMsg: this.engravingMessage, product_id: this.group.sku };
   
    BasketEventBus.post(
      BasketEvent.ShowEngravingPopup, engravingPopupData as ISFCCProductItem,
      this.applyEngraving,
      engraving3dRenderingUuid,
    );
  }

  private async handleChanges() {
    if (!this.isEnabled) {
      return;
    }

    this.engravingMessage = '';
    if (!this.group) {
      this.isEngravingAllowed = false;
      return;
    }

    const isEngravingAllowedOnProduct: boolean = await isProductAllowedForEngraving(this.group.sku);
    if (!isEngravingAllowedOnProduct) {
      this.isEngravingAllowed = false;
      return;
    }

    const groupPrice: IFromPrice | ISFCCGroupPrice | null = await fetchGroupPrice(this.group.groupId);
    const availabilityState: AvailabilityState = getAvailabilityState(groupPrice);
    this.isEngravingAllowed = availabilityState <= AvailabilityState.BackOrder;
    this.applyEngraving(this.engravingMessage);
  }

  /**
   * Checks whether an engraving feature toggle is on
   * @async
   */
  private async isEngravingFeatureOn(): Promise<boolean> {
    const features: ISiteFeature[] | null = await fetchSiteFeatures([SiteFeature.SfccBasketEngraving]);
    return Array.isArray(features) && features.length > 0 && features[0].value;
  }

  private applyEngraving = (message: string) => {
    this.engravingMessage = message;

    if (this.onEngravingMessageChange) {
      this.onEngravingMessageChange({ message: this.engravingMessage});
    }

    this.$scope.$applyAsync();
  };
}
